/**
 * アルコール測定の運用設定（測定連動の勤怠 alcohol_linked_attendance の会社のみ）。
 * 陸上がマスタ管理「アルコール測定の運用」で決めた「何分前に通知するか」「有効な測定の範囲」を
 * 案内文に出すために読む。取れなければ既定（30分前・前後30分）で表示する。
 */
import { useEffect, useState } from 'react';
import { apiFetch } from '../services/api';
import { hasFeature } from '../utils/featureFlags';

export interface AlcoholWorkflowSettings {
  reminder_minutes: number;
  window_before_min: number;
  window_after_min: number;
  notify_self: boolean;
  notify_captain: boolean;
  notify_admins: boolean;
  /** 「前後30分」「前30分〜後60分」のような表示用 */
  window_label: string;
}
export const DEFAULT_ALCOHOL_SETTINGS: AlcoholWorkflowSettings = {
  reminder_minutes: 30, window_before_min: 30, window_after_min: 30,
  notify_self: true, notify_captain: true, notify_admins: true, window_label: '前後30分',
};

let cached: AlcoholWorkflowSettings | null = null;
let inflight: Promise<AlcoholWorkflowSettings> | null = null;

export function loadAlcoholWorkflowSettings(): Promise<AlcoholWorkflowSettings> {
  if (cached) return Promise.resolve(cached);
  if (!inflight) {
    inflight = apiFetch<{ data: AlcoholWorkflowSettings }>('/alcohol-workflow/settings')
      .then(r => { cached = { ...DEFAULT_ALCOHOL_SETTINGS, ...(r?.data ?? {}) }; return cached; })
      .catch(() => DEFAULT_ALCOHOL_SETTINGS)
      .finally(() => { inflight = null; });
  }
  return inflight;
}

export function useAlcoholWorkflowSettings(): AlcoholWorkflowSettings {
  const [s, setS] = useState<AlcoholWorkflowSettings>(cached ?? DEFAULT_ALCOHOL_SETTINGS);
  useEffect(() => {
    if (!hasFeature('alcohol_linked_attendance')) return;
    let alive = true;
    loadAlcoholWorkflowSettings().then(v => { if (alive) setS(v); });
    return () => { alive = false; };
  }, []);
  return s;
}

/** 「測定がないまま終了すると陸上・船長に通知されます」の宛先部分 */
export function notifiedPartiesLabel(s: AlcoholWorkflowSettings): string {
  return [s.notify_admins ? '陸上' : '', s.notify_captain ? '船長' : ''].filter(Boolean).join('・');
}
